import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import { useSelector } from 'react-redux';
import useBenguide from 'hooks/benguide';
import { useInitialization } from 'modules/chatbot/hooks/useInitialization';

import {
  selectShowPreparingOverlay,
  selectInitializationStatus,
  selectInitializationError,
  ApiStatus,
} from 'modules/chatbot/slices/chatbotSlice';

import styles from './PreparingChatOverlay.module.less';

const PreparingChatOverlay: React.FC = () => {
  const benefitGuide = useBenguide();
  const showOverlay = useSelector(selectShowPreparingOverlay);
  const initializationStatus = useSelector(selectInitializationStatus);
  const initializationError = useSelector(selectInitializationError);
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // Initialize the assistant at the container level
  const { retry: retryInitialization } = useInitialization({
    benefitGuideId: benefitGuide?.masterId,
    employerId: benefitGuide?.employerId,
    organizationId: benefitGuide?.orgId,
    autoInitialize: false,
  });

  useEffect(() => {
    if (showOverlay) {
      setShouldRender(true);
      setIsClosing(false);
    } else if (shouldRender) {
      setIsClosing(true);
      // Wait for animation to complete before hiding
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 300); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [showOverlay, shouldRender]);

  if (!shouldRender) {
    return null;
  }

  const renderContent = () => {
    if (initializationStatus === ApiStatus.ERROR) {
      return (
        <>
          <div className={styles.errorIcon}>⚠️</div>
          <h2 className={styles.title}>Initialization Failed</h2>
          <p className={styles.description}>
            {initializationError ||
              'Failed to initialize the SAGE. Please try again.'}
          </p>
          <div className={styles.actions}>
            <Button
              type="primary"
              className={styles.retryButton}
              onClick={() => retryInitialization()}
            >
              Retry Initialization
            </Button>
          </div>
        </>
      );
    }

    return (
      <>
        <div className={styles.loadingSpinner}>
          <div className={styles.spinner}></div>
        </div>
        <h2 className={styles.title}>SAGE is getting ready</h2>
        <p className={styles.description}>
          Your personalized benefits assistant will be right with you!
        </p>
        <div className={styles.dots}>
          <span className={styles.dot}></span>
          <span className={styles.dot}></span>
          <span className={styles.dot}></span>
        </div>
      </>
    );
  };

  return (
    <div className={`${styles.overlay} ${isClosing ? styles.closing : ''}`}>
      <div className={styles.modal}>
        <div className={styles.content}>{renderContent()}</div>
      </div>
    </div>
  );
};

export default PreparingChatOverlay;
